/**
 * S2 Strategies, reduced to what exists. Backlog task 70.
 *
 * `UI-Design-Spec.md#s2--strategies-list` specifies a register with seven columns — name,
 * state, pairs, 30d return, Sharpe, max drawdown, trade count — and **four of those come from
 * a deployment**, which is M2. There is no engine, no fleet and no live P&L, so a screen that
 * printed a Sharpe here would be printing a number from nowhere.
 *
 * So the metric columns are the *last backtest's*, labelled as such, and the footnote says
 * which four the spec asks for that this cannot yet answer. That is the same choice the
 * Markets screen made about its missing hero: naming the absence beats filling the slot.
 *
 * ## The summary strip is the hero, and that is the spec's own exemption
 *
 * *"Hero: none — this screen is a list, and it is the one screen exempt from one-hero. What
 * replaces it is a one-line summary strip: 4 live · 1 paper · 12 shadow · 3 retired. The
 * conclusion for a list is its composition."* The counts here are lifecycle states that
 * actually exist — draft, validating, validated, rejected, retired — rather than deployment
 * states that do not.
 *
 * ## Sorting never reorders the live block, and there is no live block
 *
 * The spec pins live rows above the sort, *"because the question 'what is running with real
 * money' must never depend on which column was last clicked"*. Nothing can be live yet, so
 * the pinning code is not written: it would be a rule with no rows to apply to and no way to
 * tell whether it worked. Recorded here so the next reader knows it is missing on purpose.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api/client';
import type { StrategyResponse } from '../api/generated';
import { DataTable } from '../design/components/data/DataTable.jsx';
import { Num } from '../design/components/data/Num.jsx';
import { StatePill } from '../design/components/data/StatePill.jsx';
import { SummaryStrip } from '../design/components/data/SummaryStrip.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';

/** Lifecycle states that exist today. `PAPER` and `LIVE` need a deployment (M2). */
const LIFECYCLE = ['draft', 'validating', 'validated', 'rejected', 'retired'] as const;

/**
 * A retired or rejected strategy renders its metrics as em dashes and its name dimmed —
 * `DataTable`'s `participating` flag. *"They are present and readable but visibly not
 * participating: the archive of failures is valuable and should not be visually noisy."*
 */
function participating(state: string): boolean {
  return state !== 'retired' && state !== 'rejected';
}

export function StrategiesScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const strategies = useQuery({
    queryKey: ['strategies'],
    queryFn: () => api.research.listStrategies({}),
  });

  const rows = useMemo(() => {
    const items: StrategyResponse[] = strategies.data?.strategies ?? [];
    const sorted = [...items].sort((a, b) => {
      const left = sortKey === 'versions' ? a.version_count : a.name.toLowerCase();
      const right = sortKey === 'versions' ? b.version_count : b.name.toLowerCase();
      const order = left < right ? -1 : left > right ? 1 : 0;
      return sortDir === 'asc' ? order : -order;
    });
    return sorted.map((item) => ({
      id: item.id,
      name: item.name,
      state: item.state,
      versions: item.version_count,
      tags: item.tags.join(' · ') || '—',
      participating: participating(item.state),
    }));
  }, [strategies.data, sortDir, sortKey]);

  const counts = useMemo(() => {
    const items: StrategyResponse[] = strategies.data?.strategies ?? [];
    return LIFECYCLE.map((state) => ({
      count: items.filter((item) => item.state === state).length,
      label: state,
      // A rejected strategy is the one count worth reading as a number to act on.
      tone: state === 'rejected' ? ('loss' as const) : ('default' as const),
    })).filter((item) => item.count > 0);
  }, [strategies.data]);

  if (strategies.isLoading) {
    return (
      <div className="jmt-screen">
        <Skeleton count={6} />
      </div>
    );
  }
  if (strategies.isError) {
    return (
      <div className="jmt-screen">
        <ErrorPanel
          message="Could not load strategies."
          detail={String((strategies.error as Error)?.message ?? '')}
          onRetry={() => void strategies.refetch()}
        />
      </div>
    );
  }

  const items: StrategyResponse[] = strategies.data?.strategies ?? [];
  if (items.length === 0) {
    return (
      <div className="jmt-screen">
        <div className="jmt-note">
          <EmptyState>
            No strategies yet. A strategy is created through the API or by `jmt-forge` (M2); this
            screen is the register once one exists.
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="jmt-screen">
      <div className="jmt-panel">
        <SummaryStrip items={counts} />
      </div>

      {/* The table sits in a flush panel, matching S14: a lifted surface with no padding
          of its own, because the table draws its own cell padding. D66 gave the world
          depth and left this screen drawing its table straight onto the canvas. */}
      <div className="jmt-panel jmt-panel--flush">
      <DataTable
        density="comfortable"
        stickyFirst
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={(key: string) => {
          if (key === sortKey) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
          } else {
            setSortKey(key);
            setSortDir('asc');
          }
        }}
        onSelectRow={(row) => navigate(`/research?strategy=${row.id}`)}
        columns={[
          { key: 'name', label: 'Name', sortable: true },
          {
            key: 'state',
            label: 'State',
            // Second, immediately after the name: *"the state pill is the visual anchor of
            // each row"*, so scanning one column shows the composition instantly.
            render: (row) => <StatePill state={row.state.toUpperCase()} />,
          },
          {
            key: 'versions',
            label: 'Versions',
            align: 'right',
            sortable: true,
            // `dp={0}` because `Num` formats a number to two places by default and a
            // count of versions read "1.00" — correct for money, wrong for a cardinal.
            render: (row) => <Num value={row.versions} dp={0} />,
          },
          { key: 'tags', label: 'Tags' },
        ]}
        rows={rows}
      />
      </div>

      <p className="jmt-note-text">
        Four of the columns <code>UI-Design-Spec.md</code> asks for — 30-day return, Sharpe, max
        drawdown and trade count — describe a <em>deployment</em>, and deployments are M2. They are
        absent rather than filled with a strategy&rsquo;s last backtest, because a backtest number
        under a &ldquo;30d&rdquo; heading would be a claim about money that was never at risk.
      </p>
    </div>
  );
}
